import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area, RadarChart,
  Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from "recharts";
import { Download, TrendingUp, Users, Clock, Coffee, Phone, MessageSquare, Edit3, Filter } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const COLORS = ["hsl(226,100%,33%)", "hsl(355,78%,56%)", "hsl(45,96%,53%)", "hsl(174,42%,40%)", "hsl(262,52%,47%)", "hsl(30,80%,55%)", "hsl(190,60%,50%)"];

export default function ReportsPage() {
  const [searchParams] = useSearchParams();
  const [charts, setCharts] = useState(null);
  const [agentPerf, setAgentPerf] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [agentDaily, setAgentDaily] = useState({ data: [], agents: [] });
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("14");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return formatter.format(new Date());
  });
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    apiCall("get", "/agents").then(({ data }) => setAgents(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const agentParam = selectedAgent !== "all" ? `&agent_id=${selectedAgent}` : "";
        const [chartsRes, perfRes, hourlyRes, dailyRes] = await Promise.all([
          apiCall("get", `/dashboard/charts?days=${days}${dateFrom ? `&from_date=${dateFrom}` : ""}${dateTo ? `&to_date=${dateTo}` : ""}${selectedAgent !== "all" ? `&agent_id=${selectedAgent}` : ""}`),
          apiCall("get", `/reports/agent-performance?${dateFrom ? `date_from=${dateFrom}&` : ""}${dateTo ? `date_to=${dateTo}&` : ""}${selectedAgent !== "all" ? `agent_id=${selectedAgent}` : ""}`),
          apiCall("get", `/reports/hourly-calls?date=${selectedDate}`),
          apiCall("get", `/reports/agent-daily?days=${days}${selectedAgent !== "all" ? `&agent_id=${selectedAgent}` : ""}`)
        ]);
        setCharts(chartsRes.data);
        setAgentPerf(perfRes.data.agents);
        setHourlyData(hourlyRes.data.hourly);
        setAgentDaily(dailyRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [days, dateFrom, dateTo, selectedDate, selectedAgent]);

  const downloadReport = async (type) => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ report_type: type });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/download?${params}`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${type}_report.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${type} report downloaded`);
    } catch { toast.error("Download failed"); }
  };

  const downloadAgentReport = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (selectedAgent !== "all") params.set("agent_id", selectedAgent);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/agent-download?${params}`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      const agentName = agents.find(ag => ag._id === selectedAgent)?.name || "all";
      a.download = `${agentName}_call_report.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Agent report downloaded");
    } catch { toast.error("Download failed"); }
  };

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${h}h ${m}m`;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Reports & Analytics</h2>
          <p className="text-sm text-muted-foreground mt-1">Agent performance, call analytics & login tracking</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedAgent} onValueChange={setSelectedAgent}>
            <SelectTrigger className="w-44" data-testid="agent-select-filter"><SelectValue placeholder="All Agents" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agents</SelectItem>
              {agents.map(a => <SelectItem key={a._id} value={a._id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36" data-testid="days-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="14">Last 14 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Downloads & Filters */}
      <Card className="border border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">From</label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">To</label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" />
            </div>
            <Button variant="outline" onClick={() => downloadReport("calls")} data-testid="download-calls-report"><Download className="w-4 h-4 mr-2" /> Call Logs</Button>
            <Button variant="outline" onClick={() => downloadReport("clients")} data-testid="download-clients-report"><Download className="w-4 h-4 mr-2" /> Clients</Button>
            <Button variant="outline" onClick={() => downloadReport("responses")} data-testid="download-responses-report"><Download className="w-4 h-4 mr-2" /> Responses</Button>
            <Button onClick={downloadAgentReport} data-testid="download-agent-report"><Download className="w-4 h-4 mr-2" /> {selectedAgent !== "all" ? `${agents.find(a => a._id === selectedAgent)?.name} Calls` : "All Agent Calls"}</Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Call Overview</TabsTrigger>
          <TabsTrigger value="agents">Agent Reports</TabsTrigger>
          <TabsTrigger value="hourly">Hourly Analysis</TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Call Trends */}
            <Card className="border border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Call Trend</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={charts?.calls_per_day || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                      <Area type="monotone" dataKey="calls" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.15} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Agent Performance Bar */}
            <Card className="border border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Agent Call Performance</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={agentPerf}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                      <Legend />
                      <Bar dataKey="interested" fill={COLORS[3]} radius={[4, 4, 0, 0]} name="Interested" stackId="a" />
                      <Bar dataKey="not_interested" fill={COLORS[1]} radius={[0, 0, 0, 0]} name="Not Interested" stackId="a" />
                      <Bar dataKey="follow_up" fill={COLORS[2]} radius={[0, 0, 0, 0]} name="Follow Up" stackId="a" />
                      <Bar dataKey="not_connected" fill={COLORS[5]} radius={[0, 0, 0, 0]} name="Not Connected" stackId="a" />
                      <Bar dataKey="converted" fill={COLORS[4]} radius={[4, 4, 0, 0]} name="Converted" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Category Pie */}
            <Card className="border border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Client Categories</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={charts?.category_distribution || []} cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                        {(charts?.category_distribution || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip /><Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Response Pie */}
            <Card className="border border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Response Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={charts?.response_distribution || []} cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                        {(charts?.response_distribution || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip /><Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Agent Daily Line */}
            <Card className="border border-border col-span-1 lg:col-span-2">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Agent Daily Calls (Per Agent)</CardTitle></CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={agentDaily.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                      <Legend />
                      {agentDaily.agents.map((name, i) => (
                        <Line key={name} type="monotone" dataKey={name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AGENT REPORTS TAB */}
        <TabsContent value="agents" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Login Hours Bar */}
            <Card className="border border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Clock className="w-4 h-4" /> Agent Login Hours (Today)</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={agentPerf}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" label={{ value: "Hours", angle: -90, position: "insideLeft", style: { fontSize: 11 } }} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                      <Legend />
                      <Bar dataKey="active_hours" fill={COLORS[3]} radius={[4, 4, 0, 0]} name="Active Hours" />
                      <Bar dataKey="break_hours" fill={COLORS[2]} radius={[4, 4, 0, 0]} name="Break Hours" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Agent Radar */}
            <Card className="border border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Agent Comparison (Radar)</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={agentPerf}>
                      <PolarGrid stroke="hsl(var(--border))" />
                      <PolarAngleAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <PolarRadiusAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <Radar name="Total Calls" dataKey="total_calls" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.2} />
                      <Radar name="Interested" dataKey="interested" stroke={COLORS[3]} fill={COLORS[3]} fillOpacity={0.2} />
                      <Radar name="Converted" dataKey="converted" stroke={COLORS[4]} fill={COLORS[4]} fillOpacity={0.2} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Agent Detail Table */}
          <Card className="border border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Users className="w-4 h-4" /> Agent Performance Detail</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wider">Agent</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Total Calls</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Interested</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Not Int.</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Follow Up</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Not Conn.</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Converted</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Login Time</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Break Time</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Active Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agentPerf.map((a, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium text-sm">{a.name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Badge className={`text-[10px] ${a.is_online ? "bg-green-500/10 text-green-600" : "bg-gray-500/10 text-gray-500"}`}>{a.is_online ? "Online" : "Offline"}</Badge>
                          {a.on_break && <Badge className="bg-yellow-500/10 text-yellow-600 text-[10px]">Break</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm font-bold">{a.total_calls}</TableCell>
                      <TableCell className="text-sm text-green-600">{a.interested}</TableCell>
                      <TableCell className="text-sm text-red-500">{a.not_interested}</TableCell>
                      <TableCell className="text-sm text-yellow-600">{a.follow_up}</TableCell>
                      <TableCell className="text-sm text-gray-500">{a.not_connected}</TableCell>
                      <TableCell className="text-sm text-purple-600 font-medium">{a.converted}</TableCell>
                      <TableCell className="text-sm font-mono">{formatTime(a.login_seconds)}</TableCell>
                      <TableCell className="text-sm font-mono">{formatTime(a.break_seconds)}</TableCell>
                      <TableCell className="text-sm font-mono font-medium">{formatTime(a.active_seconds)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HOURLY TAB */}
        <TabsContent value="hourly" className="space-y-4 mt-4">
          <div className="flex items-center gap-3 mb-4">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Date</label>
            <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-44" data-testid="hourly-date-input" />
          </div>
          <Card className="border border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Phone className="w-4 h-4" /> Calls Per Hour - {selectedDate}</CardTitle></CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                    <Bar dataKey="calls" fill={COLORS[0]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
